import i18n from "@/src/i18n";
import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const MAX_PERCENTAGE = 100;
const MAX_FILE_SIZE_MB = 5242880;

const PlotStationSchema = z.object({
    id: z.number(),
    name: z.string().trim(),
    code: z.string().trim(),
});
export type PlotStationProps = z.output<typeof PlotStationSchema>;

export const PlotSchema = z
    .object({
        id: zIdSchema,
        name: z.string().trim().nullable(),
        documentUrl: z.string().trim().nullable(),
        geoJson: z.string().trim(),
        clayPercentage: z.number().nullable(),
        siltPercentage: z.number().nullable(),
        sandPercentage: z.number().nullable(),
        stations: z.array(PlotStationSchema),
    })
    .brand<"Plot">();

export type PlotInputProps = z.input<typeof PlotSchema>;
export type PlotProps = z.output<typeof PlotSchema>;

export class Plot {
    private constructor(private readonly props: Readonly<PlotProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): Nullable<string> {
        return this.props.name;
    }

    get documentUrl(): Nullable<string> {
        return this.props.documentUrl;
    }

    get geoJson(): string {
        return this.props.geoJson;
    }

    get clayPercentage(): Nullable<number> {
        return this.props.clayPercentage;
    }

    get siltPercentage(): Nullable<number> {
        return this.props.siltPercentage;
    }

    get sandPercentage(): Nullable<number> {
        return this.props.sandPercentage;
    }

    get stations(): readonly PlotStationProps[] {
        return this.props.stations;
    }

    static validate(
        props: Readonly<MaybeValidProps<PlotInputProps>>,
    ): Result<PlotProps, z.ZodError<PlotProps>> {
        const result = PlotSchema.safeParse(props);
        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<PlotInputProps>,
    ): Result<Plot, z.ZodError<PlotProps>> {
        return Plot.validate(props).map((validated) => new Plot(validated));
    }
}

export const PlotUpdateSchema = z
    .object({
        id: z.number().positive(),
        name: z.string().trim().optional(),
        sandPercentage: z.number().min(0).nullish(),
        siltPercentage: z.number().min(0).nullish(),
        clayPercentage: z.number().min(0).nullish(),
        stationIds: z.array(z.int().positive()).optional(),
    })
    .superRefine((plot, ctx) => {
        const { sandPercentage, siltPercentage, clayPercentage } = plot;
        const total =
            (sandPercentage ?? 0) +
            (siltPercentage ?? 0) +
            (clayPercentage ?? 0);
        if (total > MAX_PERCENTAGE) {
            ctx.addIssue({
                code: "custom",
                message: i18n.t("validation:models.plot.percentagesSum"),
                path: ["sandPercentage"],
            });
        }
    });

export type PlotUpdate = z.output<typeof PlotUpdateSchema>;

const FileSchema = z.custom<File>((value) => value instanceof File, {
    error: () => i18n.t("validation:models.plot.fileRequired"),
});

export const PlotFileUploadSchema = z.object({
    file: FileSchema.refine((file) => file.size <= MAX_FILE_SIZE_MB, {
        error: () => i18n.t("validation:models.plot.fileTooLarge"),
    }).optional(),
});
export type PlotFileUpload = z.output<typeof PlotFileUploadSchema>;

const PlotFileSchema = z
    .object({
        documentUrl: z.string().trim(),
    })
    .brand<"PlotFile">();

export type PlotFileInputProps = z.input<typeof PlotFileSchema>;
export type PlotFileProps = z.output<typeof PlotFileSchema>;

export class PlotFile {
    private constructor(private readonly props: Readonly<PlotFileProps>) {}

    get documentUrl(): string {
        return this.props.documentUrl;
    }

    static validate(
        props: Readonly<MaybeValidProps<PlotFileInputProps>>,
    ): Result<PlotFileProps, z.ZodError<PlotFileProps>> {
        const result = PlotFileSchema.safeParse(props);
        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<PlotFileInputProps>,
    ): Result<PlotFile, z.ZodError<PlotFileProps>> {
        return PlotFile.validate(props).map(
            (validated) => new PlotFile(validated),
        );
    }
}
